import { useQuery } from "@tanstack/react-query";
import { CircularProgress } from "@mui/material";

import { DimensionValueSet } from "@/lib/dimensions";
import { LegendError, LegendItem } from "@/features/shared/widgets/map/map.styles";
import {
  LoadLegendImage,
  legendImageQueryOptions,
} from "@/features/shared/widgets/map/legends/legend-image-query";
import { useTranslation } from "@/lib/i18n";
import Button from "@/components/button/button";

interface LegendImageProps {
  layerId: string;
  opacity?: number;
  loadLegendImage: LoadLegendImage;
  dimensionValues: DimensionValueSet;
  /**
   * When true, render nothing until at least one dimension value is available.
   * Model and observed layers initialise their dimensions asynchronously and
   * must not request a legend with an empty dimension set.
   */
  hideWhenNoDimensions?: boolean;
}

/**
 * Shared legend image for all map layers. The legend request uses `retry: false`,
 * so a single failure is terminal for that query key; the error state therefore
 * offers a manual retry (`refetch`) so a failed legend can be recovered without a
 * full page reload or navigating away and back.
 */
const LegendImage = ({
  layerId,
  opacity,
  loadLegendImage,
  dimensionValues,
  hideWhenNoDimensions = false,
}: LegendImageProps) => {
  const { t } = useTranslation();

  const legendQuery = useQuery({
    ...legendImageQueryOptions(layerId, dimensionValues, loadLegendImage),
    placeholderData: (previous, previousQuery) => {
      // Keep the previous image across dimension changes for the same layer, but
      // drop it when the layer changes so a stale legend is never shown.
      if (previousQuery?.queryKey[1] === layerId) return previous;
      return undefined;
    },
    retry: false,
  });

  if (hideWhenNoDimensions && Object.keys(dimensionValues).length === 0) {
    return null;
  }

  if (legendQuery.isError) {
    return (
      <LegendError>
        <span>{t("map.legend_load_error")}</span>
        <Button
          variant="ghost"
          loading={legendQuery.isFetching}
          onClick={() => {
            void legendQuery.refetch();
          }}
        >
          {t("map.legend_retry")}
        </Button>
      </LegendError>
    );
  }

  if (legendQuery.isLoading) return <CircularProgress size={24} color="inherit" />;
  if (!legendQuery.data) return null;

  return (
    <LegendItem>
      <img src={legendQuery.data} style={{ opacity: opacity ?? 1.0 }} />
    </LegendItem>
  );
};

export default LegendImage;
